import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { Loader2, Minus, Plus } from 'lucide-react'
import VoiceSectionPage from '@/components/layout/VoiceSectionPage'
import {
  getAreaLocalFromLocalidade,
  getLocalidadeLabel,
  shouldIncludeLocalidade,
} from '@/utils/brdidLocalidades'
import { fetchAvailableDids, fetchLocalidades } from '@/utils/brdidListApi'
import { getVoicePlanById } from '@/config/voicePlans'
import { useAuth } from '@/contexts/AuthContext'
import { supabase } from '@/supabaseClient'

function toBRL(value) {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0)
}

function formatDidNumber(value) {
  const digits = String(value || '').replace(/\D/g, '')
  if (digits.length === 10) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`
  }
  if (digits.length === 11) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`
  }
  return value || ''
}

const BR_UF_OPTIONS = [
  'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO',
  'MA', 'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI',
  'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO',
]

const UF_NAME_TO_CODE = {
  acre: 'AC',
  alagoas: 'AL',
  amapa: 'AP',
  amazonas: 'AM',
  bahia: 'BA',
  ceara: 'CE',
  'distrito federal': 'DF',
  espirito_santo: 'ES',
  goias: 'GO',
  maranhao: 'MA',
  mato_grosso: 'MT',
  mato_grosso_do_sul: 'MS',
  minas_gerais: 'MG',
  para: 'PA',
  paraiba: 'PB',
  parana: 'PR',
  pernambuco: 'PE',
  piaui: 'PI',
  rio_de_janeiro: 'RJ',
  rio_grande_do_norte: 'RN',
  rio_grande_do_sul: 'RS',
  rondonia: 'RO',
  roraima: 'RR',
  santa_catarina: 'SC',
  sao_paulo: 'SP',
  sergipe: 'SE',
  tocantins: 'TO',
}

function digitsOnly(value) {
  return String(value || '').replace(/\D/g, '')
}

function formatCep(value) {
  const d = digitsOnly(value).slice(0, 8)
  if (d.length <= 5) return d
  return `${d.slice(0, 5)}-${d.slice(5)}`
}

function formatCpfCnpj(value) {
  const d = digitsOnly(value).slice(0, 14)
  if (d.length <= 11) {
    return d
      .replace(/^(\d{3})(\d)/, '$1.$2')
      .replace(/^(\d{3})\.(\d{3})(\d)/, '$1.$2.$3')
      .replace(/\.(\d{3})(\d)/, '.$1-$2')
  }
  return d
    .replace(/^(\d{2})(\d)/, '$1.$2')
    .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/\.(\d{3})(\d)/, '.$1/$2')
    .replace(/(\d{4})(\d)/, '$1-$2')
}

function formatPhoneBr(value) {
  const d = digitsOnly(value).slice(0, 11)
  if (d.length <= 2) return d
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`
}

function normalizeUf(value) {
  const raw = String(value || '').trim()
  if (!raw) return ''
  const upper = raw.toUpperCase()
  if (BR_UF_OPTIONS.includes(upper)) return upper
  const normalized = raw
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, '_')
  return UF_NAME_TO_CODE[normalized] || ''
}

export default function PlanCheckoutSummaryPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const { getProfileId } = useAuth()
  const profileId = getProfileId()

  const planId = location.state?.planId
  const billingCycle = location.state?.billingCycle === 'annual' ? 'annual' : 'monthly'
  const plan = getVoicePlanById(planId)

  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [activeNumbers, setActiveNumbers] = useState([])
  const [addonCount, setAddonCount] = useState(0)
  const [localidades, setLocalidades] = useState([])
  const [defaultAreaLocal, setDefaultAreaLocal] = useState('31000')
  const [didsByArea, setDidsByArea] = useState({})
  const [loadingAreas, setLoadingAreas] = useState({})
  const [includedSlots, setIncludedSlots] = useState([])
  const [addonSlots, setAddonSlots] = useState([])

  const [brdidBillingOpen, setBrdidBillingOpen] = useState(false)
  const [brdidBillingSaving, setBrdidBillingSaving] = useState(false)
  const [cepLookupLoading, setCepLookupLoading] = useState(false)
  const [brdidBilling, setBrdidBilling] = useState({
    brdid_name: '',
    brdid_email: '',
    brdid_phone: '',
    brdid_cpf_cnpj: '',
    brdid_postal_code: '',
    brdid_street: '',
    brdid_number: '',
    brdid_complement: '',
    brdid_neighborhood: '',
    brdid_city: '',
    brdid_region: '',
    brdid_invoice_cutoff_day: 15,
  })

  const hasExistingNumber = activeNumbers.length > 0
  const includedByPlan = plan?.includedNumbers ?? 0
  const autoIncludedCount = Math.min(activeNumbers.length, includedByPlan)
  const includedSelectableCount = Math.max(0, includedByPlan - autoIncludedCount)
  const requiredDidSelections = includedSelectableCount + addonCount
  const inflightAreasRef = useRef(new Set())

  useEffect(() => {
    if (!plan || plan.id === 'enterprise') {
      navigate('/planos', { replace: true })
      return
    }

    async function bootstrap() {
      if (!profileId) {
        setLoading(false)
        return
      }
      setLoading(true)
      const [numbersRes, locsRes, profileRes, brdidBillingRes] = await Promise.all([
        supabase
          .from('whatsapp_numbers')
          .select('id, did_number, status')
          .eq('profile_id', profileId)
          .in('status', ['active', 'grace']),
        fetchLocalidades().catch(() => []),
        supabase
          .from('profiles')
          .select('*')
          .eq('id', profileId)
          .single(),
        supabase
          .from('brdid_client_billing_profiles')
          .select('*')
          .eq('profile_id', profileId)
          .maybeSingle(),
      ])

      if (numbersRes.error) {
        console.error('[PlanCheckoutSummary] numbers:', numbersRes.error)
        toast.error('Falha ao carregar numeros da conta.')
      } else {
        setActiveNumbers(numbersRes.data || [])
      }

      const profile = profileRes?.data || null
      const savedBrdid = brdidBillingRes?.data || null
      const fromProfileName = String(profile?.company_name || profile?.full_name || '').trim()
      const fromProfileEmail = String(profile?.email || '').trim()
      const fromProfilePhone = String(profile?.phone || '').trim()

      setBrdidBilling((prev) => ({
        ...prev,
        brdid_name: savedBrdid?.brdid_name || fromProfileName || prev.brdid_name,
        brdid_email: savedBrdid?.brdid_email || fromProfileEmail || prev.brdid_email,
        brdid_phone: formatPhoneBr(savedBrdid?.brdid_phone || fromProfilePhone || prev.brdid_phone),
        brdid_cpf_cnpj: formatCpfCnpj(savedBrdid?.brdid_cpf_cnpj || prev.brdid_cpf_cnpj),
        brdid_postal_code:
          formatCep(savedBrdid?.brdid_postal_code || profile?.address_postal_code || prev.brdid_postal_code),
        brdid_street: savedBrdid?.brdid_street || profile?.address_street || prev.brdid_street,
        brdid_number: savedBrdid?.brdid_number || profile?.address_number || prev.brdid_number,
        brdid_complement: savedBrdid?.brdid_complement || prev.brdid_complement,
        brdid_neighborhood:
          savedBrdid?.brdid_neighborhood || profile?.address_locality || prev.brdid_neighborhood,
        brdid_city: savedBrdid?.brdid_city || profile?.address_city || prev.brdid_city,
        brdid_region: normalizeUf(
          savedBrdid?.brdid_region ||
            profile?.address_region_code ||
            profile?.address_region ||
            prev.brdid_region
        ),
        brdid_invoice_cutoff_day:
          savedBrdid?.brdid_invoice_cutoff_day || prev.brdid_invoice_cutoff_day || 15,
      }))

      const locs = Array.isArray(locsRes) ? locsRes : []
      const uniqueByArea = new Map()
      for (const item of locs) {
        if (!shouldIncludeLocalidade(item)) continue
        const area = getAreaLocalFromLocalidade(item)
        if (!area) continue
        if (!uniqueByArea.has(area)) uniqueByArea.set(area, item)
      }
      const uniqueLocalidades = Array.from(uniqueByArea.values()).sort((a, b) =>
        getAreaLocalFromLocalidade(a).localeCompare(getAreaLocalFromLocalidade(b))
      )
      setLocalidades(uniqueLocalidades)
      if (uniqueLocalidades.length > 0) {
        setDefaultAreaLocal(getAreaLocalFromLocalidade(uniqueLocalidades[0]) || '31000')
      }
      setLoading(false)
    }

    bootstrap()
  }, [navigate, plan, profileId])

  const requiredBrdidFields = useMemo(
    () => [
      { key: 'brdid_name', label: 'Nome' },
      { key: 'brdid_email', label: 'Email' },
      { key: 'brdid_phone', label: 'Telefone' },
      { key: 'brdid_cpf_cnpj', label: 'CPF/CNPJ' },
      { key: 'brdid_postal_code', label: 'CEP' },
      { key: 'brdid_street', label: 'Endereço' },
      { key: 'brdid_number', label: 'Número do endereço' },
      { key: 'brdid_neighborhood', label: 'Bairro' },
      { key: 'brdid_city', label: 'Cidade' },
      { key: 'brdid_region', label: 'Estado/UF' },
    ],
    []
  )

  const missingBrdidFields = useMemo(() => {
    return requiredBrdidFields
      .filter((f) => {
        const v = String(brdidBilling[f.key] ?? '')
        return !v.trim()
      })
      .map((f) => f.label)
  }, [brdidBilling, requiredBrdidFields])

  useEffect(() => {
    if (missingBrdidFields.length > 0) setBrdidBillingOpen(true)
  }, [missingBrdidFields.length])

  async function saveBrdidBillingProfile() {
    if (!profileId) return
    setBrdidBillingSaving(true)
    try {
      const payload = {
        profile_id: profileId,
        brdid_name: brdidBilling.brdid_name || null,
        brdid_email: brdidBilling.brdid_email || null,
        brdid_phone: digitsOnly(brdidBilling.brdid_phone) || null,
        brdid_cpf_cnpj: digitsOnly(brdidBilling.brdid_cpf_cnpj) || null,
        brdid_postal_code: formatCep(brdidBilling.brdid_postal_code) || null,
        brdid_street: brdidBilling.brdid_street || null,
        brdid_number: brdidBilling.brdid_number || null,
        brdid_complement: brdidBilling.brdid_complement || null,
        brdid_neighborhood: brdidBilling.brdid_neighborhood || null,
        brdid_city: brdidBilling.brdid_city || null,
        brdid_region: (brdidBilling.brdid_region || '').toUpperCase() || null,
        brdid_invoice_cutoff_day: brdidBilling.brdid_invoice_cutoff_day || 15,
      }
      const { error } = await supabase
        .from('brdid_client_billing_profiles')
        .upsert(payload, { onConflict: 'profile_id' })
      if (error) throw error
      toast.success('Dados BRDID salvos.')
      return true
    } catch (e) {
      console.error('[PlanCheckoutSummary] BRDID billing save error:', e)
      toast.error('Falha ao salvar dados BRDID.')
      return false
    } finally {
      setBrdidBillingSaving(false)
    }
  }

  useEffect(() => {
    const cepDigits = digitsOnly(brdidBilling.brdid_postal_code)
    if (cepDigits.length !== 8) return

    let cancelled = false
    async function lookupCep() {
      setCepLookupLoading(true)
      try {
        const res = await fetch(`https://viacep.com.br/ws/${cepDigits}/json/`)
        const data = await res.json()
        if (cancelled || !data || data.erro) return
        setBrdidBilling((prev) => ({
          ...prev,
          brdid_street: prev.brdid_street || data.logradouro || '',
          brdid_neighborhood: prev.brdid_neighborhood || data.bairro || '',
          brdid_city: prev.brdid_city || data.localidade || '',
          brdid_region: prev.brdid_region || data.uf || '',
          brdid_complement: prev.brdid_complement || data.complemento || '',
        }))
      } catch (e) {
        console.error('[PlanCheckoutSummary] CEP lookup error:', e)
      } finally {
        if (!cancelled) setCepLookupLoading(false)
      }
    }
    lookupCep()
    return () => {
      cancelled = true
    }
  }, [brdidBilling.brdid_postal_code])

  function didToNumber(item) {
    if (!item) return ''
    if (typeof item === 'string') return item.replace(/\D/g, '')
    const cn = String(item.CN || '').replace(/\D/g, '')
    const num = String(item.NUMERO || '').replace(/\D/g, '')
    return `${cn}${num}`
  }

  function didLabel(item) {
    if (typeof item === 'string') return item
    const cn = String(item.CN || '')
    const num = String(item.NUMERO || '')
    return [cn && `(${cn})`, num].filter(Boolean).join(' ')
  }

  const ensureDidsForArea = useCallback(
    async (areaLocal) => {
      const code = String(areaLocal || '').trim()
      if (!code || didsByArea[code] || inflightAreasRef.current.has(code)) return
      inflightAreasRef.current.add(code)
      setLoadingAreas((prev) => ({ ...prev, [code]: true }))
      try {
        const list = await fetchAvailableDids(code).catch(() => [])
        const map = new Map()
        for (const item of Array.isArray(list) ? list : []) {
          const number = didToNumber(item)
          if (!number) continue
          if (!map.has(number)) map.set(number, didLabel(item))
        }
        const options = Array.from(map.entries()).map(([number, label]) => ({ number, label }))
        setDidsByArea((prev) => ({ ...prev, [code]: options }))
      } finally {
        inflightAreasRef.current.delete(code)
        setLoadingAreas((prev) => ({ ...prev, [code]: false }))
      }
    },
    [didsByArea]
  )

  function clampAddonCount(nextCount) {
    setAddonCount(Math.max(0, Math.min(10, nextCount)))
  }

  function updateSlotArea(kind, index, areaLocal) {
    const setFn = kind === 'included' ? setIncludedSlots : setAddonSlots
    setFn((prev) => {
      const next = [...prev]
      next[index] = { ...next[index], areaLocal, did: '' }
      return next
    })
  }

  function updateSlotDid(kind, index, did) {
    const setFn = kind === 'included' ? setIncludedSlots : setAddonSlots
    setFn((prev) => {
      const next = [...prev]
      next[index] = { ...next[index], did }
      return next
    })
  }

  const monthlyBase = plan?.monthlyPrice || 0
  const annualBase = monthlyBase * 10
  const addOnsMonthly = addonCount * (plan?.extraNumberPrice || 0)
  const totalNow = billingCycle === 'annual' ? annualBase + addOnsMonthly * 12 : monthlyBase + addOnsMonthly

  const selectedIncludedDids = useMemo(() => includedSlots.map((slot) => slot.did).filter(Boolean), [includedSlots])
  const selectedAddonDids = useMemo(() => addonSlots.map((slot) => slot.did).filter(Boolean), [addonSlots])
  const selectedAllDids = useMemo(() => [...selectedIncludedDids, ...selectedAddonDids], [selectedAddonDids, selectedIncludedDids])
  const selectedDidPreview = useMemo(
    () => [
      ...includedSlots
        .filter((slot) => slot.did)
        .map((slot, idx) => ({ key: `inc-${idx}`, label: `Incluso ${idx + 1}`, did: slot.did })),
      ...addonSlots
        .filter((slot) => slot.did)
        .map((slot, idx) => ({ key: `add-${idx}`, label: `Add-on ${idx + 1}`, did: slot.did })),
    ],
    [addonSlots, includedSlots]
  )
  const selectedDidCount = selectedAllDids.length
  const missingDidCount = Math.max(0, requiredDidSelections - selectedDidCount)
  const canCheckout =
    !!plan &&
    !!profileId &&
    selectedIncludedDids.length === includedSelectableCount &&
    selectedAddonDids.length === addonCount &&
    missingBrdidFields.length === 0 &&
    !submitting

  function getSlotOptions(kind, index) {
    const slots = kind === 'included' ? includedSlots : addonSlots
    const current = slots[index]?.did || ''
    const currentArea = slots[index]?.areaLocal || defaultAreaLocal
    const areaOptions = didsByArea[currentArea] || []
    const taken = new Set(selectedAllDids.filter((did) => did && did !== current))
    return areaOptions.filter((opt) => !taken.has(opt.number))
  }

  useEffect(() => {
    setIncludedSlots((prev) => {
      const next = prev.slice(0, includedSelectableCount)
      while (next.length < includedSelectableCount) next.push({ areaLocal: defaultAreaLocal, did: '' })
      return next
    })
  }, [defaultAreaLocal, includedSelectableCount])

  useEffect(() => {
    setAddonSlots((prev) => {
      const next = prev.slice(0, addonCount)
      while (next.length < addonCount) next.push({ areaLocal: defaultAreaLocal, did: '' })
      return next
    })
  }, [addonCount, defaultAreaLocal])

  useEffect(() => {
    const areas = new Set()
    for (const slot of [...includedSlots, ...addonSlots]) {
      if (slot?.areaLocal) areas.add(slot.areaLocal)
    }
    for (const area of areas) ensureDidsForArea(area)
  }, [addonSlots, ensureDidsForArea, includedSlots])

  async function handleGoStripe() {
    if (!plan) return
    if (!profileId) return toast.error('Perfil nao identificado.')
    if (missingBrdidFields.length > 0) {
      setBrdidBillingOpen(true)
      toast.error(`Preencha os dados BRDID para continuar: ${missingBrdidFields.join(', ')}`)
      return
    }
    if (selectedIncludedDids.length !== includedSelectableCount) return toast.error('Selecione todos os DIDs inclusos pendentes.')
    if (selectedAddonDids.length !== addonCount) return toast.error('Selecione todos os DIDs adicionais.')

    const priceId = billingCycle === 'annual' ? plan.annualPriceId : plan.monthlyPriceId
    if (!priceId) return toast.error(`Preco ${billingCycle === 'annual' ? 'anual' : 'mensal'} nao configurado.`)

    const savedOk = await saveBrdidBillingProfile()
    if (!savedOk) return

    setSubmitting(true)
    const { data, error } = await supabase.functions.invoke('create-stripe-checkout', {
      body: {
        priceId,
        subscription_metadata: {
          voice_plan_code: plan.id,
          voice_billing_cycle: billingCycle,
          voice_included_did: selectedIncludedDids[0] || '',
          voice_included_dids: selectedIncludedDids.join(','),
          voice_addon_dids: selectedAddonDids.join(','),
          voice_addon_count: String(addonCount),
        },
      },
    })
    setSubmitting(false)

    if (error) return toast.error(error.message || 'Erro ao iniciar checkout.')
    if (!data?.checkout_url) return toast.error('URL de checkout nao recebida.')
    window.location.href = data.checkout_url
  }

  return (
    <VoiceSectionPage
      title={`Resumo da contratacao - ${plan?.name || ''}`}
      description="Revise plano, ciclo, numeros incluidos e add-ons antes de ir para o checkout."
    >
      {loading ? (
        <div className="rounded-xl border border-border bg-card p-6 text-sm text-muted-foreground">
          Carregando resumo...
        </div>
      ) : (
        <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_360px]">
          <div className="space-y-5">
            <div className="rounded-xl border border-border bg-card p-4 text-sm text-muted-foreground">
              {hasExistingNumber
                ? `Conta ja possui ${activeNumbers.length} numero(s). ${autoIncludedCount} sera(ao) usado(s) automaticamente como incluso(s) do plano.`
                : 'Conta sem numero ativo: escolha abaixo os DIDs inclusos do plano.'}
            </div>

            <div className="rounded-xl border border-border bg-card p-4 space-y-3">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm font-medium text-foreground">Add-ons (novos numeros)</p>
                  <p className="text-xs text-muted-foreground">Escolha quantos numeros adicionais deseja contratar.</p>
                </div>
                <div className="inline-flex items-center gap-2 self-start rounded-lg border border-input bg-background p-1">
                  <button
                    type="button"
                    onClick={() => clampAddonCount(addonCount - 1)}
                    className="inline-flex h-9 w-9 items-center justify-center rounded-md text-muted-foreground hover:bg-muted"
                    aria-label="Diminuir add-ons"
                  >
                    <Minus className="h-4 w-4" />
                  </button>
                  <input
                    type="text"
                    readOnly
                    value={addonCount}
                    className="h-9 w-12 rounded-md border border-input bg-background text-center text-sm font-semibold text-foreground"
                    aria-label="Quantidade de add-ons"
                  />
                  <button
                    type="button"
                    onClick={() => clampAddonCount(addonCount + 1)}
                    className="inline-flex h-9 w-9 items-center justify-center rounded-md text-muted-foreground hover:bg-muted"
                    aria-label="Aumentar add-ons"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                Cada adicional: {toBRL(plan?.extraNumberPrice || 0)}/mes
              </p>
            </div>

            <details
              className="rounded-xl border border-border bg-card p-4"
              open={brdidBillingOpen}
              onToggle={(e) => setBrdidBillingOpen((e.target).open)}
            >
              <summary className="cursor-pointer list-none flex items-start justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold text-foreground">Dados para faturamento BRDID</p>
                  <p className="text-xs text-muted-foreground">Preencha o que faltar para criar o cliente no Billing da BRDID.</p>
                </div>
                <span className="rounded-full border border-border bg-muted/30 px-2.5 py-1 text-[11px] font-medium text-muted-foreground">
                  {missingBrdidFields.length > 0 ? `Faltam ${missingBrdidFields.length}` : 'Pronto'}
                </span>
              </summary>

              <div className="mt-4 space-y-3">
                <div className="grid gap-3 sm:grid-cols-2">
                  <label className="block">
                    <span className="text-xs text-muted-foreground">Nome</span>
                    <input
                      value={brdidBilling.brdid_name}
                      onChange={(e) => setBrdidBilling((p) => ({ ...p, brdid_name: e.target.value }))}
                      className="mt-2 h-10 w-full rounded-lg border border-input bg-background px-3 text-sm"
                    />
                  </label>
                  <label className="block">
                    <span className="text-xs text-muted-foreground">Email</span>
                    <input
                      value={brdidBilling.brdid_email}
                      onChange={(e) => setBrdidBilling((p) => ({ ...p, brdid_email: e.target.value }))}
                      className="mt-2 h-10 w-full rounded-lg border border-input bg-background px-3 text-sm"
                    />
                  </label>
                  <label className="block">
                    <span className="text-xs text-muted-foreground">Telefone</span>
                    <input
                      value={brdidBilling.brdid_phone}
                      onChange={(e) =>
                        setBrdidBilling((p) => ({ ...p, brdid_phone: formatPhoneBr(e.target.value) }))
                      }
                      className="mt-2 h-10 w-full rounded-lg border border-input bg-background px-3 text-sm"
                      placeholder="(31) 99999-9999"
                    />
                  </label>
                  <label className="block">
                    <span className="text-xs text-muted-foreground">CPF/CNPJ</span>
                    <input
                      value={brdidBilling.brdid_cpf_cnpj}
                      onChange={(e) =>
                        setBrdidBilling((p) => ({ ...p, brdid_cpf_cnpj: formatCpfCnpj(e.target.value) }))
                      }
                      className="mt-2 h-10 w-full rounded-lg border border-input bg-background px-3 text-sm"
                      placeholder="000.000.000-00 ou 00.000.000/0000-00"
                    />
                  </label>
                </div>

                <div className="grid gap-3 sm:grid-cols-2">
                  <label className="block">
                    <span className="text-xs text-muted-foreground">CEP</span>
                    <input
                      value={brdidBilling.brdid_postal_code}
                      onChange={(e) =>
                        setBrdidBilling((p) => ({ ...p, brdid_postal_code: formatCep(e.target.value) }))
                      }
                      className="mt-2 h-10 w-full rounded-lg border border-input bg-background px-3 text-sm"
                      placeholder="00000-000"
                    />
                    {cepLookupLoading ? (
                      <span className="mt-1 inline-block text-[11px] text-muted-foreground">Buscando endereço...</span>
                    ) : null}
                  </label>
                  <label className="block">
                    <span className="text-xs text-muted-foreground">Endereço</span>
                    <input
                      value={brdidBilling.brdid_street}
                      onChange={(e) => setBrdidBilling((p) => ({ ...p, brdid_street: e.target.value }))}
                      className="mt-2 h-10 w-full rounded-lg border border-input bg-background px-3 text-sm"
                    />
                  </label>
                  <label className="block">
                    <span className="text-xs text-muted-foreground">Número</span>
                    <input
                      value={brdidBilling.brdid_number}
                      onChange={(e) => setBrdidBilling((p) => ({ ...p, brdid_number: e.target.value }))}
                      className="mt-2 h-10 w-full rounded-lg border border-input bg-background px-3 text-sm"
                    />
                  </label>
                  <label className="block">
                    <span className="text-xs text-muted-foreground">Complemento (opcional)</span>
                    <input
                      value={brdidBilling.brdid_complement}
                      onChange={(e) => setBrdidBilling((p) => ({ ...p, brdid_complement: e.target.value }))}
                      className="mt-2 h-10 w-full rounded-lg border border-input bg-background px-3 text-sm"
                    />
                  </label>
                  <label className="block">
                    <span className="text-xs text-muted-foreground">Bairro</span>
                    <input
                      value={brdidBilling.brdid_neighborhood}
                      onChange={(e) => setBrdidBilling((p) => ({ ...p, brdid_neighborhood: e.target.value }))}
                      className="mt-2 h-10 w-full rounded-lg border border-input bg-background px-3 text-sm"
                    />
                  </label>
                  <label className="block">
                    <span className="text-xs text-muted-foreground">Cidade</span>
                    <input
                      value={brdidBilling.brdid_city}
                      onChange={(e) => setBrdidBilling((p) => ({ ...p, brdid_city: e.target.value }))}
                      className="mt-2 h-10 w-full rounded-lg border border-input bg-background px-3 text-sm"
                    />
                  </label>
                  <label className="block">
                    <span className="text-xs text-muted-foreground">Estado/UF</span>
                    <select
                      value={brdidBilling.brdid_region}
                      onChange={(e) => setBrdidBilling((p) => ({ ...p, brdid_region: e.target.value }))}
                      className="mt-2 h-10 w-full rounded-lg border border-input bg-background px-3 text-sm"
                    >
                      <option value="">Selecione a UF</option>
                      {BR_UF_OPTIONS.map((uf) => (
                        <option key={uf} value={uf}>
                          {uf}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>

                <div className="flex flex-wrap items-center justify-end gap-2">
                  <button
                    type="button"
                    onClick={saveBrdidBillingProfile}
                    disabled={brdidBillingSaving || missingBrdidFields.length > 0}
                    className="inline-flex min-h-[44px] items-center gap-2 rounded-lg border border-border bg-background px-4 py-2 text-sm font-medium disabled:opacity-50"
                  >
                    {brdidBillingSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Salvar dados'}
                  </button>
                </div>
              </div>
            </details>

            {requiredDidSelections > 0 ? (
              <div className="rounded-xl border border-border bg-card p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold text-foreground">Selecao de numeros</p>
                  <span className="rounded-full border border-border bg-muted/30 px-2.5 py-1 text-[11px] font-medium text-muted-foreground">
                    Passo essencial
                  </span>
                </div>
                <div className="rounded-lg border border-border bg-muted/20 px-3 py-2 text-xs text-muted-foreground">
                  Selecionados: {selectedDidCount}/{requiredDidSelections}
                  {missingDidCount > 0 ? ` - faltam ${missingDidCount}` : ' - completo'}
                </div>

                {localidades.length === 0 ? (
                  <div className="rounded-lg border border-dashed border-border bg-background p-4 text-sm text-muted-foreground">
                    Nenhum DDD/localidade disponivel no momento.
                  </div>
                ) : (
                  <div className="space-y-3">
                    {includedSelectableCount > 0
                      ? includedSlots.map((slot, index) => (
                          <div key={`included-slot-${index}`} className="rounded-lg border border-border bg-background p-3 grid gap-2 sm:grid-cols-2">
                            <label className="block">
                              <span className="text-sm font-medium text-foreground">DDD incluso {index + 1}</span>
                              <select
                                value={slot.areaLocal}
                                onChange={(e) => updateSlotArea('included', index, e.target.value)}
                                className="mt-2 h-10 w-full rounded-lg border border-input bg-background px-3 text-sm"
                              >
                                {localidades.map((item, idx) => (
                                  <option key={`${getAreaLocalFromLocalidade(item)}-inc-${idx}`} value={getAreaLocalFromLocalidade(item)}>
                                    {getLocalidadeLabel(item)}
                                  </option>
                                ))}
                              </select>
                            </label>
                            <label className="block">
                              <span className="text-sm font-medium text-foreground">DID incluso {index + 1}</span>
                              <select
                                value={slot.did}
                                onChange={(e) => updateSlotDid('included', index, e.target.value)}
                                className="mt-2 h-10 w-full rounded-lg border border-input bg-background px-3 text-sm"
                              >
                                <option value="">{loadingAreas[slot.areaLocal] ? 'Carregando DIDs...' : 'Selecione o DID incluso'}</option>
                                {getSlotOptions('included', index).map((opt) => (
                                  <option key={`${opt.number}-incdid-${index}`} value={opt.number}>
                                    {opt.label}
                                  </option>
                                ))}
                              </select>
                            </label>
                          </div>
                        ))
                      : null}

                    {addonSlots.map((slot, index) => (
                      <div key={`addon-slot-${index}`} className="rounded-lg border border-border bg-background p-3 grid gap-2 sm:grid-cols-2">
                        <label className="block">
                          <span className="text-sm font-medium text-foreground">DDD add-on {index + 1}</span>
                          <select
                            value={slot.areaLocal}
                            onChange={(e) => updateSlotArea('addon', index, e.target.value)}
                            className="mt-2 h-10 w-full rounded-lg border border-input bg-background px-3 text-sm"
                          >
                            {localidades.map((item, idx) => (
                              <option key={`${getAreaLocalFromLocalidade(item)}-add-${idx}`} value={getAreaLocalFromLocalidade(item)}>
                                {getLocalidadeLabel(item)}
                              </option>
                            ))}
                          </select>
                        </label>
                        <label className="block">
                          <span className="text-sm font-medium text-foreground">DID add-on {index + 1}</span>
                          <select
                            value={slot.did}
                            onChange={(e) => updateSlotDid('addon', index, e.target.value)}
                            className="mt-2 h-10 w-full rounded-lg border border-input bg-background px-3 text-sm"
                          >
                            <option value="">{loadingAreas[slot.areaLocal] ? 'Carregando DIDs...' : 'Selecione o DID adicional'}</option>
                            {getSlotOptions('addon', index).map((opt) => (
                              <option key={`${opt.number}-adddid-${index}`} value={opt.number}>
                                {opt.label}
                              </option>
                            ))}
                          </select>
                        </label>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : null}

            <div className="flex flex-wrap justify-end gap-2">
              <button
                type="button"
                onClick={() => navigate('/planos')}
                className="inline-flex min-h-[44px] items-center rounded-lg border border-border bg-background px-4 py-2 text-sm font-medium"
              >
                Voltar
              </button>
              <button
                type="button"
                onClick={handleGoStripe}
                disabled={!canCheckout}
                className="inline-flex min-h-[44px] items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-50"
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                Ir para checkout
              </button>
            </div>
          </div>

          <aside className="lg:sticky lg:top-20 lg:self-start">
            <div className="rounded-xl border border-border bg-card p-4 space-y-4">
              <div>
                <p className="text-xs text-muted-foreground">Plano</p>
                <p className="text-base font-semibold text-foreground">{plan?.name}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Ciclo</p>
                <p className="text-sm font-medium text-foreground">{billingCycle === 'annual' ? 'Anual' : 'Mensal'}</p>
              </div>
              <div className="grid gap-2 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Numeros inclusos</span>
                  <span className="font-medium text-foreground">{includedByPlan}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Inclusos automaticos</span>
                  <span className="font-medium text-foreground">{autoIncludedCount}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Inclusos para escolher</span>
                  <span className="font-medium text-foreground">{includedSelectableCount}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Add-ons</span>
                  <span className="font-medium text-foreground">{addonCount}</span>
                </div>
              </div>
              <div className="rounded-lg border border-border bg-background p-3">
                <p className="text-xs text-muted-foreground">Numeros selecionados</p>
                {selectedDidPreview.length === 0 ? (
                  <p className="mt-1 text-sm text-muted-foreground">Nenhum numero selecionado ainda.</p>
                ) : (
                  <ul className="mt-2 space-y-1.5">
                    {selectedDidPreview.map((item) => (
                      <li key={item.key} className="flex items-center justify-between gap-3 text-sm">
                        <span className="text-muted-foreground">{item.label}</span>
                        <span className="font-medium text-foreground">{formatDidNumber(item.did)}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div className="rounded-lg border border-border bg-muted/20 p-3 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Plano</span>
                  <span className="font-medium text-foreground">{toBRL(billingCycle === 'annual' ? annualBase : monthlyBase)}</span>
                </div>
                <div className="mt-1 flex items-center justify-between">
                  <span className="text-muted-foreground">Add-ons</span>
                  <span className="font-medium text-foreground">{toBRL(billingCycle === 'annual' ? addOnsMonthly * 12 : addOnsMonthly)}</span>
                </div>
              </div>
              <div className="border-t border-border pt-3">
                <p className="text-xs text-muted-foreground">Total</p>
                <p className="text-xl font-bold text-foreground">
                  {toBRL(totalNow)} {billingCycle === 'annual' ? '/ano' : '/mes'}
                </p>
              </div>
            </div>
          </aside>
        </div>
      )}
    </VoiceSectionPage>
  )
}
